import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { forms } from 'seal-ui-components';
import Divider from '@Components/UI/Divider';
import { SingleSelectDropdown } from '@Components/UI/Dropdowns';
import Permission from '@Constants/permissions';
import { NOTIFICATION_HANDLER } from '@Constants/types';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import { stopAnimation, resetAnimation, getSealViews } from '@Actions/documentActions';
import MetaDataList from './components/MetaDataList';
import { AuthorizePage } from '@Components/Authorize';
import { isAuthorized } from '@Service/authentication';
import store from '@Store';
import AnalyzeDocument from '@Components/AnalyzeDocument';
import { Title, MetaDataContainerWrapper } from './MetaDataPanel.style';
import { MIX_PANEL } from '@Constants/types';
import mixpanelEvents from '@Mixpanel';

const viewsPermission = [Permission.VIEWS_ALL_VIEW, Permission.VIEWS_READ];

const MetadataBlock = React.memo(
    ({ allLabels }) =>
        Boolean(allLabels && allLabels.length && allLabels.some(x => x.values.length > 0)) && (
            <MetaDataList isSubGroup={false} metalist={allLabels} />
        )
);

const MetaDataPanel = () => {
    const [showNonStandardOnly, setShowNonStandardOnly] = useState(false);
    const [selectedSealView, setSelectedSealView] = useState({});
    const [selectedSealViewValue, setSelectedSealViewValue] = useState('');
    const [allSealViews, setAllSealViews] = useState({});

    const dispatch = useDispatch();

    const { user } = useSelector(state => state.auth);
    const { userViews, metadata, documentStatus } = useSelector(state => state.document);

    const { items } = metadata;

    const constructLabelsFilterDropdown = () => {
        const isAuthorizedForAllViewsOnly = isAuthorized(user, [Permission.VIEWS_ALL_VIEW], []);

        const allSealViewsInput =
            items && isAuthorizedForAllViewsOnly ? [{ key: 'All', text: 'All Labels' }] : [];
        if (userViews) {
            userViews.forEach(userView => {
                allSealViewsInput.push({
                    key: userView.id,
                    text: userView.name,
                    labels: userView.metadataItems
                });
            });
            setAllSealViews(allSealViewsInput);
        }
    };

    useEffect(
        () => {
            if (userViews && userViews.length === 0 && isAuthorized(user, viewsPermission, []))
                dispatch(getSealViews());
            constructLabelsFilterDropdown();
        },
        [items, userViews && userViews.length, user.id]
    );

    useEffect(
        () => {
            setSelectedSealView(allSealViews[0]);
        },
        [allSealViews.length, setSelectedSealView]
    );

    const handlerSealViewsDropdownOnChange = option => {
        try {
            const mixpanel = {
                labelViewId: selectedSealView.key
            };
            store.dispatch({
                type: MIX_PANEL.MIXPANEL_DATA_LABELS,
                payload: mixpanel
            });

            mixpanelEvents.selectview();

            if (option.key === 'All') {
                setSelectedSealView(option);
                setSelectedSealViewValue(option.text);
                return;
            }

            setSelectedSealView(allSealViews.filter(item => item.key === option.key)[0]);
            setSelectedSealViewValue(option.text);
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'MetaDataPanel.handlerSealViewsDropdownOnChange', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };

    const nonStandardToggleOnChange = () => {
        setShowNonStandardOnly(!showNonStandardOnly);
    };

    const getMetadata = (metadataInput, name) => {
        if (metadataInput) return metadataInput.find(item => name === item.name);
        return null;
    };

    const handlerBuildMetadataArray = () => {
        const MetadataArray = [];

        if (items) {
            const FilteredMetadata = JSON.parse(JSON.stringify(items));
            // Filtering metadata according to standrad vs non-standard toogle
            if (FilteredMetadata && showNonStandardOnly) {
                FilteredMetadata.forEach(metadataItem => {
                    metadataItem.values = metadataItem.values.filter(item => item.nonStandard);
                });
            }

            if (selectedSealView && selectedSealView.key === 'All') return FilteredMetadata;
            // Filtering metadata according to the selected seal view
            if (selectedSealView && selectedSealView.labels) {
                selectedSealView.labels.forEach(viewItem => {
                    const MetadataItem = getMetadata(FilteredMetadata, viewItem.name);
                    if (MetadataItem && viewItem.subgroups.length > 0) {
                        MetadataItem.subgroups = [];
                        viewItem.subgroups.forEach(viewSubGroup => {
                            const MetadataItemSubGroup = {};
                            MetadataItemSubGroup.name = viewSubGroup.name;
                            MetadataItemSubGroup.items = [];

                            viewSubGroup.metadataItems.forEach(viewSubGroupItem => {
                                const MetadataItemSubGroupItem = getMetadata(
                                    items,
                                    viewSubGroupItem.name
                                );
                                if (MetadataItemSubGroupItem)
                                    MetadataItemSubGroup.items.push(MetadataItemSubGroupItem);
                            });

                            if (MetadataItemSubGroup && MetadataItemSubGroup.items.length > 0)
                                MetadataItem.subgroups.push(MetadataItemSubGroup);
                        });
                    }
                    if (MetadataItem) MetadataArray.push(MetadataItem);
                });
            }
        }
        return MetadataArray;
    };

    const allLabels = React.useMemo(handlerBuildMetadataArray, [
        items,
        selectedSealView && selectedSealView.key,
        showNonStandardOnly
    ]);
    const isThereIsNonStandard = () =>
        items &&
        Array.isArray(items) &&
        !!items.reduce((filtered, option) => {
            if (option.values.filter(item => item.nonStandard).length) {
                filtered.push(option);
            }
            return filtered;
        }, []).length;

    return (
        <AuthorizePage havePermission={true}>
            <AnalyzeDocument
                statues={['Initializing', 'Analyzing document', '']}
                documentStatus={documentStatus}
                pageType="labels"
                data={metadata}
                stopAnimation={stopAnimation}
                resetAnimation={resetAnimation}
            >
                <MetaDataContainerWrapper>
                    {isThereIsNonStandard() && (
                        <>
                            <div data-testid="non_standard_policies_filter_button">
                                <forms.ToggleField
                                    className="custom-toggle"
                                    value={showNonStandardOnly}
                                    label="Show only with non-standard policies"
                                    options={[
                                        { value: true, label: '' },
                                        { value: false, label: '' }
                                    ]}
                                    size={1.7}
                                    onChange={nonStandardToggleOnChange}
                                />
                            </div>
                            <Divider />
                        </>
                    )}
                    <Title>Select view</Title>
                    <SingleSelectDropdown
                        placeholder="Select view"
                        selectedValue={selectedSealViewValue}
                        options={allSealViews}
                        onChange={handlerSealViewsDropdownOnChange}
                    />
                    <MetadataBlock allLabels={allLabels} />
                </MetaDataContainerWrapper>
            </AnalyzeDocument>
        </AuthorizePage>
    );
};

export default MetaDataPanel;
